/** Hooks */
import { useLocationMutation, useLocationQuery } from '@features/redux/apis/org';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

/** Base Components */
import { Button, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { FormRequiredText } from '@components/forms/text/base/FormRequiredText';
import { ManageForm } from '@components/forms/ManageForm';
import { FormDisabledText } from '@components/forms/text/base/FormDisabledText';
import { SingleDatePicker } from '@components/SingleDatePicker';

/** Fixed Scope Components */
import { PinInput } from '@components/forms/number/PinInput';
import { OrgDropDowns } from '@components/forms/dropdowns/OrgDropdowns';
import { CountrySelect } from '@components/forms/dropdowns/CountrySelect';
import { LocationEffects } from './LocationEffects';
import { LocationSystems } from './LocationSystems';

/** Utils, Consts */
import { Operation } from '@/types/general';
import { getIdDisplayValue } from '@utils/index';
import { Validate } from '@utils/validate';
import { Query } from '@features/redux/apis/utils';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';
import dayjs from 'dayjs';

const useLocations = Query.withDraft(useLocationQuery, {
    LocationName: '',
    LocationID: 0,
    Country: '',
    PIN: '',
    CompanyID: 0,
    Inactive: false,
    ConsentToSell: null,
});

export const Location = ({ operation, id: locationId, onSuccess }: ManageProps) => {
    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useLocations({ locationId: +locationId! }, { skip: operation === Operation.Create });

    const [mutate, { isLoading: mutationLoading }] = useLocationMutation({
        fixedCacheKey: operation === Operation.Edit ? 'LocationMutation' : 'LocationCreation',
    });

    const [cts, setCts] = useState(false);
    useEffect(() => void setCts(Boolean(data?.ConsentToSell)), [data?.ConsentToSell]);

    const isLoading = queryLoading || mutationLoading;

    const [draftState, setDraftState] = draft;

    return (
        <>
            <LocationEffects />

            <ManageForm
                title={operation === Operation.Create ? 'Register New Location' : 'Manage Location'}
                size={12}
                loading={isLoading}>
                <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                    <Grid container spacing={2} marginTop='10px'>
                        <Grid item xs={12} sm={12} md={12}>
                            <Typography fontWeight='bold'>Location Details:</Typography>
                        </Grid>
                        {operation === Operation.Edit && (
                            <FormDisabledText size={12} label='ID' value={getIdDisplayValue(data?.LocationID)} />
                        )}

                        <FormRequiredText
                            size={12}
                            stateKey='LocationName'
                            draft={draft}
                            label='Location Name'
                            validateFn={Validate.name}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid location name',
                            }}
                        />

                        <OrgDropDowns
                            options={{
                                companies: true,
                                resellers: true,
                                locations: false,
                            }}
                            draft={draft}
                        />

                        <CountrySelect draft={draft} idKey='Country' />

                        <PinInput draft={draft} showLock={operation === Operation.Edit} />

                        {role.isAdmin && role.prices.edit && (
                            <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                                <FormControlLabel
                                    sx={{ userSelect: 'none' }}
                                    checked={cts}
                                    onChange={({ target }) => {
                                        const checked = (target as HTMLInputElement).checked;
                                        setCts(checked);

                                        // ? Clear CTS date if the checkbox is unchecked
                                        if (!checked) {
                                            setDraftState((prev) => ({ ...prev, ConsentToSell: null }));
                                        }
                                    }}
                                    control={<Checkbox />}
                                    label='Consent To Sell Agreement'
                                />

                                {cts && (
                                    <SingleDatePicker
                                        date={
                                            draftState.ConsentToSell && dayjs.tz(draftState.ConsentToSell).toISOString()
                                        }
                                        setDate={(ConsentToSell) =>
                                            setDraftState((prev) => ({ ...prev, ConsentToSell }))
                                        }
                                    />
                                )}
                            </Grid>
                        )}

                        <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                            <Button
                                type='submit'
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                disabled={!Validate.Org.location(draftState)}
                                size={'large'}
                                variant={'contained'}
                                color={'orange'}
                                onClick={async () => {
                                    await mutate({
                                        method: operation === Operation.Create ? 'POST' : 'PATCH',
                                        location: draftState,
                                    });

                                    onSuccess?.();
                                }}>
                                {operation === Operation.Create ? 'Create' : 'Update'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                {operation !== Operation.Create && (
                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                        <LocationSystems locationId={locationId} />
                    </Grid>
                )}
            </ManageForm>
        </>
    );
};
